type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

export type ApiError = {
  status: number;
  message: string;
  body?: unknown;
};

type Tokens = { accessToken: string; refreshToken: string } | null;

let apiBaseUrl = "";
let getTokens: () => Tokens = () => null;
let setTokens: (tokens: Tokens) => void = () => undefined;
let onUnauthorized: () => void = () => undefined;

export function configureApiClient(opts: {
  baseUrl: string;
  getTokens: () => Tokens;
  setTokens: (tokens: Tokens) => void;
  onUnauthorized: () => void;
}) {
  apiBaseUrl = opts.baseUrl.replace(/\/+$/, "");
  getTokens = opts.getTokens;
  setTokens = opts.setTokens;
  onUnauthorized = opts.onUnauthorized;
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function refreshOnce(refreshToken: string): Promise<Tokens> {
  const res = await fetch(`${apiBaseUrl}/auth/user/refreshToken`, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken })
  });

  if (!res.ok) return null;
  const body = (await parseJsonSafe(res)) as any;
  if (!body?.accessToken || !body?.refreshToken) return null;
  return { accessToken: String(body.accessToken), refreshToken: String(body.refreshToken) };
}

export async function apiFetch<T = Json>(
  path: string,
  init: RequestInit & { json?: unknown; timeoutMs?: number } = {}
): Promise<T> {
  if (!apiBaseUrl) {
    throw new Error("API client not configured (missing baseUrl)");
  }

  const tokens = getTokens();
  const headers = new Headers(init.headers);

  const timeoutMs = init.timeoutMs;
  const controller = timeoutMs ? new AbortController() : null;
  const timeout =
    timeoutMs && controller
      ? window.setTimeout(() => controller.abort(), timeoutMs)
      : null;

  const signal = init.signal ?? controller?.signal;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (tokens?.accessToken) {
    headers.set("Authorization", `Bearer ${tokens.accessToken}`);
  }

  const doRequest = async () =>
    fetch(`${apiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`, {
      ...init,
      mode: "cors",
      credentials: "omit",
      headers,
      signal,
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body
    });

  let res = await doRequest();

  if (timeout) window.clearTimeout(timeout);

  if (res.status === 401 && tokens?.refreshToken) {
    const next = await refreshOnce(tokens.refreshToken);
    if (next) {
      setTokens(next);
      headers.set("Authorization", `Bearer ${next.accessToken}`);
      res = await doRequest();
    } else {
      onUnauthorized();
    }
  }

  if (!res.ok) {
    const body = await parseJsonSafe(res);
    const err: ApiError = {
      status: res.status,
      message: (body as any)?.message ? String((body as any).message) : res.statusText,
      body
    };
    throw err;
  }

  return (await parseJsonSafe(res)) as T;
}

export async function apiFetchFormData<T = Json>(
  path: string,
  formData: FormData,
  init: Omit<RequestInit, "body" | "method"> & { method?: string } = {}
): Promise<T> {
  if (!apiBaseUrl) {
    throw new Error("API client not configured (missing baseUrl)");
  }

  const tokens = getTokens();
  const headers = new Headers(init.headers);

  // IMPORTANT: do not set Content-Type manually. Browser must add multipart boundary.
  if (tokens?.accessToken) {
    headers.set("Authorization", `Bearer ${tokens.accessToken}`);
  }

  const method = init.method ?? "POST";
  const doRequest = async () =>
    fetch(`${apiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`, {
      ...init,
      method,
      mode: "cors",
      credentials: "omit",
      headers,
      body: formData,
    });

  let res = await doRequest();

  // Refresh-once retry for 401s.
  if (res.status === 401 && tokens?.refreshToken) {
    const next = await refreshOnce(tokens.refreshToken);
    if (next) {
      setTokens(next);
      headers.set("Authorization", `Bearer ${next.accessToken}`);
      res = await doRequest();
    } else {
      onUnauthorized();
    }
  }

  if (!res.ok) {
    const body = await parseJsonSafe(res);
    const err: ApiError = {
      status: res.status,
      message: (body as any)?.message ? String((body as any).message) : res.statusText,
      body,
    };
    throw err;
  }

  return (await parseJsonSafe(res)) as T;
}

